import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import { filesApi } from '@/lib/api';

const KIND_META = {
  'ai-revision': { icon: 'wand-magic-sparkles', text: 'AI edit' },
  restore: { icon: 'clock-rotate-left', text: 'Restore point' },
  import: { icon: 'file-import', text: 'Import' },
};

function relativeTime(iso) {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/**
 * Dropdown listing an artifact's saved versions (snapshots taken before
 * each AI revision / restore) with one-click restore. Used by the document,
 * presentation, and spreadsheet editors.
 */
export default function EditorVersionHistory({ fileId, onRestored, onClose }) {
  const { colors } = useTheme();
  const [versions, setVersions] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [restoringIndex, setRestoringIndex] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await filesApi.listFileVersions(fileId);
        if (!cancelled) setVersions(list);
      } catch (_) {
        if (!cancelled) setError('Could not load version history.');
      }
    })();
    return () => { cancelled = true; };
  }, [fileId]);

  const restore = async (index) => {
    setRestoringIndex(index);
    setError(null);
    try {
      const file = await filesApi.restoreFileVersion(fileId, index);
      onRestored?.(file);
    } catch (_) {
      setError('Restore failed. Please try again.');
      setRestoringIndex(null);
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Version history"
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: 6,
        width: 300,
        maxHeight: 340,
        overflowY: 'auto',
        padding: 6,
        borderRadius: 10,
        background: colors.solid_color_primary,
        border: `1px solid ${colors.border_color_secondary}`,
        boxShadow: '0 10px 32px rgba(15, 23, 42, 0.3)',
        zIndex: 95,
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '4px 8px 8px',
        borderBottom: `1px solid ${colors.border_color_secondary}`,
        marginBottom: 4,
      }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: colors.text_color_primary }}>
          Version history
        </span>
        <button
          onClick={onClose}
          aria-label="Close version history"
          style={{ border: 0, background: 'transparent', cursor: 'pointer', padding: 4, display: 'grid', placeItems: 'center' }}
        >
          <Icon name="xmark" size={12} color={colors.text_color_secondary} />
        </button>
      </div>

      {error ? (
        <div style={{ padding: '8px 10px', fontSize: 12, color: '#dc2626' }}>{error}</div>
      ) : null}

      {versions === null && !error ? (
        <div style={{ padding: '12px 10px', fontSize: 12, color: colors.text_color_secondary }}>
          Loading…
        </div>
      ) : null}

      {versions?.length === 0 ? (
        <div style={{ padding: '12px 10px', fontSize: 12, color: colors.text_color_secondary, lineHeight: 1.5 }}>
          No saved versions are available for this file.
        </div>
      ) : null}

      {(versions || []).slice().reverse().map((version) => {
        const meta = KIND_META[version.kind] || KIND_META['ai-revision'];
        return (
          <div
            key={version.index}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              padding: '8px 8px',
              borderRadius: 8,
            }}
          >
            <div style={{
              width: 26,
              height: 26,
              borderRadius: 7,
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              background: colors.solid_color_block,
            }}>
              <Icon name={meta.icon} size={12} color={colors.text_color_secondary} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 12,
                fontWeight: 600,
                color: colors.text_color_primary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {version.label || meta.text}
              </div>
              <div style={{ fontSize: 10.5, color: colors.text_color_secondary }}>
                {meta.text} · {relativeTime(version.at)}
              </div>
            </div>
            <button
              className="gab-editor-btn"
              onClick={() => restore(version.index)}
              disabled={restoringIndex !== null}
            >
              {restoringIndex === version.index ? 'Restoring…' : 'Restore'}
            </button>
          </div>
        );
      })}
    </div>
  );
}
